import React, { useEffect, useRef } from 'react';
import { MetaData, MockService } from 'types/rest/mockServiceTypes';
import _ from 'lodash';
import { WithTranslation, withTranslation } from 'react-i18next';
import { CallbackFn, useFormInput, ValidatorType } from 'hooks/useFormInput';
import i18n from 'i18n';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import { addStyleUsingJs, portRange } from 'utils/mockServiceUtils';
import acApi from '../../../../../axios/ac/acApi';
import { getErrorMessage } from '../../../../../utils/miscUtil';

export type PreferredPortType = 'group' | 'mockService';

export interface LocationPort {
  harborId: string;
  shipId: string;
  port: string;
  excludeMockServiceIds?: number[];
  excludeMockServiceGroupIds?: number[];
}

interface PreferredPortCellProps {
  id: string;
  mockService: MockService;
  preferredPort: string;
  onChange: (rowData: MockService, preferredPort: string) => void;
  isEditable: boolean;
  disabled: boolean;
  className?: string;
  isMandatory?: boolean;
  type: PreferredPortType;
  title?: string;
}

class MockPreferredPortCell extends React.Component<PreferredPortCellProps & WithTranslation> {
  state = {
    preferredPort: this.props.preferredPort,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Readonly<PreferredPortCellProps & WithTranslation>): void {
    if (String(this.props.preferredPort) !== String(this.state.preferredPort)) {
      this.setState({ preferredPort: nextProps.preferredPort });
    }
  }

  preferredPortChangedHandler = (preferredPort: string) => {
    if (String(this.state.preferredPort) !== String(preferredPort)) {
      this.setState({ preferredPort: preferredPort }, () => {
        this.triggerUpdate(preferredPort);
      });
    }
  };

  triggerUpdate = _.debounce((preferredPort: string) => {
    this.props.onChange(this.props.mockService, preferredPort);
  }, 600);

  render() {
    let portRangeVal = '';
    if (this.props.mockService.harborId && this.props.mockService.shipId) {
      portRangeVal = portRange(this.props.mockService.harborId, this.props.mockService.shipId);
    }

    if (this.props.isEditable) {
      return (
        <PreferredPortInputWithValidationHook
          id={this.props.id}
          mockService={this.props.mockService}
          portRangeVal={portRangeVal}
          preferredPort={this.state.preferredPort ? String(this.state.preferredPort) : ''}
          disabled={this.props.disabled}
          preferredPortChangedHandler={this.preferredPortChangedHandler}
          className={this.props.className}
          isMandatory={this.props.isMandatory}
          title={this.props.title}
          locPort={
            {
              harborId: this.props.mockService.harborId,
              shipId: this.props.mockService.shipId,
            } as LocationPort
          }
          type={this.props.type}
        />
      );
    } else {
      return <div></div>;
    }
  }
}

export default withTranslation('mockServices')(MockPreferredPortCell);

/**
 * Input validation hook. It is functional component wrapping BzFormControlWithHook which cannot be
 * used inside the stateful component.
 * @constructor
 */
const PreferredPortInputWithValidationHook = (props: {
  preferredPort: string;
  portRangeVal: string;
  disabled: boolean;
  id: string;
  preferredPortChangedHandler: any;
  className?: string;
  isMandatory?: boolean;
  locPort?: LocationPort;
  type: PreferredPortType;
  mockService: MockService;
  title?: string;
}) => {
  /**
   * Use effect for applying changes coming from outside.
   * */
  useEffect(() => {
    if (String(preferredPortInput.value) !== String(props.preferredPort)) {
      preferredPortInput.resetValue(props.preferredPort);
    }
  }, [props.preferredPort]);

  useEffect(() => {
    if (props.portRangeVal !== '' && String(preferredPortInput.value) === String(props.preferredPort)) {
      preferredPortInput.validate(String(preferredPortInput.value));
    }
  }, [props.portRangeVal]);

  const formId = 'formId' + props.id;
  const callValidation = async (locPort: LocationPort) => {
    try {
      return (await acApi.location.postValidateLocationPort(locPort)).data.result;
    } catch (e) {
      const msg = getErrorMessage(e);
      console.error(msg);
      return false;
    }
  };

  const fieldPortValue = useRef(props.preferredPort);
  const verifyPort = (valPort: string) => {
    if (_.isEmpty(valPort)) {
      return;
    }

    let locationPort = { ...props.locPort, port: valPort };
    //For mockService object, use the mock service ID
    if (props.type === 'mockService') {
      const id = props.mockService.isDraft ? props.mockService.serviceMockId : props.mockService.id;
      locationPort = { ...locationPort, excludeMockServiceIds: id ? [id] : undefined };
    } else if (props.type === 'group') {
      locationPort = { ...locationPort, excludeMockServiceGroupIds: [props.mockService.id] };
    }

    callValidation(locationPort).then(isValid => {
      if (isValid === true) {
        //Do update
        if (fieldPortValue.current === valPort) {
          addStyleUsingJs(formId, '0');
          props.preferredPortChangedHandler(valPort);
        }
      } else {
        if (fieldPortValue.current === valPort) {
          preferredPortInput.overrideValidation({
            isValid: false,
            invalidMessage: i18n.t('mockServices:mockServicesTable.preferredPorInUse'),
          });
        }
      }
    });
  };

  const verifyPortRef = useRef(_.debounce(verifyPort, 400));

  const preferredPortInput = useFormInput(
    props.preferredPort,
    (fn: CallbackFn) => {
      fieldPortValue.current = String(fn.value);
      if (!fn.isValid) {
        addStyleUsingJs(formId, '17');
      }
      if (fn.isValid) {
        addStyleUsingJs(formId, '0');
        verifyPortRef.current(String(fn.value));
      }
    },
    {
      type: ValidatorType.MAX_LENGTH,
      value: 5,
      invalidMessage: i18n.t('mockServices:mockServicesTable.preferredPortMaxLength'),
    },
    {
      type: ValidatorType.INTEGER,
      invalidMessage: 'Port must be a number',
    },
    {
      type: ValidatorType.INVALID_PORT_RANGE,
      invalidMessage: i18n.t('mockServices:mockServicesTable.preferredPortOutOfRange') + props.portRangeVal + '.',
      range: props.portRangeVal,
    },
  );
  let placeHolder = '';
  if (props.portRangeVal !== '') {
    placeHolder =
      props.isMandatory === true
        ? i18n.t('mockServices:mockServicesTable.placeHolderMandatory') + props.portRangeVal
        : props.portRangeVal + i18n.t('mockServices:mockServicesTable.preferredPortPlaceHolder');
  }

  const portRange = props.portRangeVal;
  const toolTip = props.title
    ? props.title
    : i18n.t('mockServices:mockServicesTable.preferredPortTooltip', { portRange: portRange });
  return (
    <>
      <BzFormControlWithHook
        id={props.id}
        useFormInputHook={preferredPortInput}
        placeholder={placeHolder}
        required={false}
        value={props.preferredPort + ''}
        disabled={props.disabled}
        title={toolTip}
        hideErrors={true}
        className={props.className}
        autoComplete="off"
        tooltipEnabled={true}
      />
      {!preferredPortInput.valid && (
        <div style={{ paddingLeft: '192px' }} className="invalid-feedback">
          {' '}
          {preferredPortInput.invalidMessage}
        </div>
      )}
    </>
  );
};
